import { Button, Divider, Flex, Input, Popconfirm, Space, Table, Tag, Typography } from 'antd';
import React, { useState, useEffect } from 'react';
import { GoDotFill } from 'react-icons/go';
import { HiOutlineTrash } from 'react-icons/hi';
import { RiPencilLine } from 'react-icons/ri';
import ManageUserModal from '../../components/ManageUserModal';
import UserAvatar from '../../components/UserAvatar';
import { getRoleByValue } from '../../helper';
import useUsers from '../../hooks/useUsers';
import { deleteUser, getUsers } from '../../redux/actions/auth';
import { getHighlightedText, integerToBoolean } from '../../utils';
import { FiSearch } from 'react-icons/fi';
import { rolesOption } from '../../constants';

const ManageUsers = () => {
  const { users, pagination } = useUsers();
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [editUser, setEditUser] = useState();
  const [filteredInfo, setFilteredInfo] = useState({});

  useEffect(() => {
    getUsers();
  }, []);

  const handleTableChange = async (pagination, filters, sorter) => {
    await getUsers({
      page: pagination.current,
      searchText: filters?.name?.length ? filters?.name[0] : '',
      searchEmail: filters?.email?.length ? filters?.email[0] : '',
      role: filters?.role?.length ? filters?.role[0] : '',
      status: filters?.status?.length ? filters?.status[0] : '',
    });
    setFilteredInfo(filters);
  };

  const columns = [
    {
      title: 'Full Name',
      dataIndex: 'name',
      key: 'name',
      width: 250,
      ellipsis: true,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div className="task-header-search">
          <Input
            placeholder="Search name..."
            size="large"
            allowClear
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys([e.target.value?.trimStart()])}
            onPressEnter={() => confirm()}
            suffix={<FiSearch />}
          />
          <Space>
            <Button type="primary" onClick={() => confirm()} icon={<FiSearch />} size="small">
              Search
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                confirm();
              }}
              color="danger"
              variant="filled"
              size="small"
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => <FiSearch style={{ color: filtered ? '#1677ff' : undefined }} />,
      onFilter: (value, record) => {
        return `${record.first_name} ${record.last_name ?? ''}`
          .toLowerCase()
          .includes(value.toLowerCase());
      },
      render: (_, record) => {
        const searchText = filteredInfo?.name?.[0]?.toLowerCase();
        const fullName = `${record.first_name} ${record.last_name ?? ''}`;
        return (
          <Flex align="center" gap={10}>
            <UserAvatar size={20} user={record} />
            <Typography.Text strong ellipsis>
              {getHighlightedText(fullName, searchText)}
            </Typography.Text>
          </Flex>
        );
      },
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 250,
      ellipsis: true,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div className="task-header-search">
          <Input
            placeholder="Search email..."
            size="large"
            allowClear
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys([e.target.value?.trimStart()])}
            onPressEnter={() => confirm()}
            suffix={<FiSearch />}
          />
          <Space>
            <Button type="primary" onClick={() => confirm()} icon={<FiSearch />} size="small">
              Search
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                confirm();
              }}
              color="danger"
              variant="filled"
              size="small"
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => <FiSearch style={{ color: filtered ? '#1677ff' : undefined }} />,
      onFilter: (value, record) => record.email.toLowerCase().includes(value.toLowerCase()),
      render: (email, record) => {
        const searchText = filteredInfo?.email?.[0]?.toLowerCase();
        return (
          <Typography.Link href={`mailto:${email}`} ellipsis>
            {getHighlightedText(email, searchText)}
          </Typography.Link>
        );
      },
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      width: 150,
      ellipsis: true,
      render: (phone) => <Typography.Text ellipsis>{phone}</Typography.Text>,
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      width: 150,
      ellipsis: true,
      filters: rolesOption.map((role) => ({ text: role.label, value: role.value })),
      onFilter: (value, record) => record.role === value,
      render: (role) => <Typography.Text ellipsis>{getRoleByValue(role)}</Typography.Text>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 150,
      ellipsis: true,
      filters: [
        { text: 'Active', value: 1 },
        { text: 'In Active', value: 0 },
      ],
      onFilter: (value, record) => integerToBoolean(record.status) === integerToBoolean(value),
      render: (enabled) => {
        const isEnabled = integerToBoolean(enabled);
        return (
          <Tag
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
            color={!isEnabled ? 'red' : 'green'}
          >
            <GoDotFill />
            {isEnabled ? 'Active' : 'In Active'}
          </Tag>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      ellipsis: true,
      align: 'center',
      render: (record) => (
        <Space>
          <Button
            size="small"
            variant="filled"
            color="primary"
            icon={<RiPencilLine />}
            onClick={() => {
              setEditUser(record);
              setIsInviteModalOpen(true);
            }}
          />
          <Popconfirm
            title="Are you sure you want to delete this user?"
            onConfirm={() => deleteUser({ id: record.id })}
          >
            <Button size="small" variant="filled" color="danger" icon={<HiOutlineTrash />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Flex align="center" justify="space-between">
        <Typography.Title level={3}>Manage Users</Typography.Title>
        <Button type="primary" onClick={() => setIsInviteModalOpen(true)}>
          Add User
        </Button>
      </Flex>
      <Divider />
      <Table
        bordered
        pagination={{
          size: 'small',
          current: pagination?.page,
          pageSize: pagination?.limit,
          total: pagination?.total,
          showSizeChanger: false,
          showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} Records`,
        }}
        columns={columns}
        dataSource={users?.map((user) => ({ ...user, key: user.id }))}
        scroll={{ x: 'max-content', y: 'calc(100vh - 300px)' }}
        onChange={handleTableChange}
        components={{
          header: {
            cell: (props) => (
              <th
                {...props}
                style={{
                  ...props.style,
                  background: 'white',
                }}
              />
            ),
          },
          body: {
            cell: (props) => (
              <td
                {...props}
                style={{
                  ...props.style,
                  padding: '4px 8px',
                }}
              />
            ),
          },
        }}
      />
      {isInviteModalOpen && (
        <ManageUserModal
          open={isInviteModalOpen}
          onCancel={() => {
            setIsInviteModalOpen(false);
            setEditUser(undefined);
          }}
          userData={editUser}
        />
      )}
    </>
  );
};

export default ManageUsers;
